import {
  type AnyRouteDescriptor,
  api,
  newId,
  type RouteClientInput,
  type RouteResponse,
} from '@agentic/contracts';
import { callRoute, type RawCallInput, routeUrl, type TransportOptions } from './http.ts';
import { type OpenEventStreamOptions, openEventStream } from './sse.ts';
import type { CallInput, ClientTree, EventStreamHandle, EventStreamHandlers } from './types.ts';

type Api = typeof api;

/** The typed client: one function per route descriptor in `api`, plus generic helpers. */
export type ApiClient = Omit<ClientTree<Api>, 'tasks'> & {
  tasks: Omit<ClientTree<Api['tasks']>, 'events'> & {
    /** Subscribes to `GET /tasks/{taskId}/events`. */
    events: (
      input: { params: RouteClientInput<Api['tasks']['events']>['params'] },
      handlers: EventStreamHandlers,
    ) => EventStreamHandle;
  };
  /** Calls any descriptor (also ones outside `api`, e.g. pack routes). */
  call<D extends AnyRouteDescriptor>(descriptor: D, input?: CallInput<D>): Promise<RouteResponse<D>>;
  /** Opens any SSE descriptor. */
  stream<D extends AnyRouteDescriptor>(
    descriptor: D,
    input: CallInput<D>,
    handlers: EventStreamHandlers,
  ): EventStreamHandle;
  /** Absolute or base-relative URL of a route, e.g. for links. */
  url<D extends AnyRouteDescriptor>(descriptor: D, input?: CallInput<D>): string;
  readonly options: Readonly<ApiClientOptions>;
};

export interface ApiClientOptions
  extends TransportOptions,
    Pick<OpenEventStreamOptions, 'retryMs' | 'maxRetryMs'> {}

function isDescriptor(value: unknown): value is AnyRouteDescriptor {
  return (
    typeof value === 'object' &&
    value !== null &&
    'method' in value &&
    'path' in value &&
    'response' in value &&
    'id' in value
  );
}

function toRaw(input: unknown): RawCallInput {
  return (input ?? {}) as RawCallInput;
}

/**
 * Builds the typed client from the contract's `api` object at runtime, so a new descriptor in
 * `@agentic/contracts` is callable without code changes here.
 *
 * ```ts
 * const client = createApiClient({ baseUrl: '/api' });
 * const view = await client.tasks.get({ params: { taskId } });
 * ```
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const streamFor = (descriptor: AnyRouteDescriptor, input: unknown, handlers: EventStreamHandlers) =>
    openEventStream(routeUrl(options.baseUrl, descriptor, toRaw(input)), handlers, options);

  const build = (node: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      if (isDescriptor(value)) {
        out[key] =
          value.responseType === 'sse'
            ? (input: unknown, handlers: EventStreamHandlers) => streamFor(value, input, handlers)
            : (input?: unknown) => callRoute(value, toRaw(input), options);
      } else if (typeof value === 'object' && value !== null) {
        out[key] = build(value as Record<string, unknown>);
      }
    }
    return out;
  };

  const tree = build(api as unknown as Record<string, unknown>);
  return Object.assign(tree, {
    call: <D extends AnyRouteDescriptor>(descriptor: D, input?: CallInput<D>) =>
      callRoute(descriptor, toRaw(input), options),
    stream: <D extends AnyRouteDescriptor>(
      descriptor: D,
      input: CallInput<D>,
      handlers: EventStreamHandlers,
    ) => streamFor(descriptor, input, handlers),
    url: <D extends AnyRouteDescriptor>(descriptor: D, input?: CallInput<D>) =>
      routeUrl(options.baseUrl, descriptor, toRaw(input)),
    options,
  }) as unknown as ApiClient;
}

/** Fresh idempotency key for `POST /tasks` (UUID v7, printable ASCII). */
export function newIdempotencyKey(prefix = 'wb'): string {
  return `${prefix}-${newId()}`;
}
